import { clampRgba, type Rgba } from '../color.ts';
import { clamp } from '../math.ts';
import { sanitizeBoatName } from './name.ts';
import { SIDE_VIEW_PARTS } from './sideView.ts';
import type { BoatDocument } from './types.ts';

export const BOAT_LENGTH_MIN_M = 3;
export const BOAT_LENGTH_MAX_M = 24;
export const BOAT_BEAM_MIN_M = 1;
export const BOAT_BEAM_MAX_M = 8;
export const BOAT_DRAFT_MIN_M = 0.1;
export const BOAT_DRAFT_MAX_M = 3;
export const BOAT_PLANING_MIN_KMH = 10;
export const BOAT_PLANING_MAX_KMH = 120;
export const BOAT_REACH_MIN_M = 4;
export const BOAT_REACH_MAX_M = 60;
export const BOAT_MEASURE_STEP_M = 0.1;

/** Factory fills from `assets/svg/boat.svg`. */
export const FACTORY_HULL_HEX = '#2c4558';
export const FACTORY_DECK_HEX = '#d5e2ec';
export const FACTORY_ACCENT_HEX = '#5aa8c8';

export const FACTORY_HULL: Rgba = { r: 44, g: 69, b: 88, a: 1 };
export const FACTORY_DECK: Rgba = { r: 213, g: 226, b: 236, a: 1 };
export const FACTORY_ACCENT: Rgba = { r: 90, g: 168, b: 200, a: 1 };

export const SKIFF_LENGTH_M = 7;
export const SKIFF_BEAM_M = 3.5;
export const SKIFF_DRAFT_M = 0.5;
export const SKIFF_PLANING_KMH = 72;
export const SKIFF_REACH_M = 30;

/** Widest beam the length still allows. Beam stays one step shorter than the length. */
export function maxBeamM(lengthM: number): number {
  return Math.min(
    BOAT_BEAM_MAX_M,
    Math.max(BOAT_BEAM_MIN_M, lengthM - BOAT_MEASURE_STEP_M),
  );
}

function clampMeasure(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }

  return clamp(value, min, max);
}

/** Pulls every authored number and colour into range. Beam cannot reach the length. */
export function normalizeBoat(boat: BoatDocument): void {
  boat.name = sanitizeBoatName(boat.name);
  boat.lengthM = clampMeasure(boat.lengthM, BOAT_LENGTH_MIN_M, BOAT_LENGTH_MAX_M, SKIFF_LENGTH_M);
  boat.beamM = clampMeasure(boat.beamM, BOAT_BEAM_MIN_M, maxBeamM(boat.lengthM), SKIFF_BEAM_M);
  boat.draftM = clampMeasure(boat.draftM, BOAT_DRAFT_MIN_M, BOAT_DRAFT_MAX_M, SKIFF_DRAFT_M);
  boat.planingSpeedKmh = clampMeasure(
    boat.planingSpeedKmh,
    BOAT_PLANING_MIN_KMH,
    BOAT_PLANING_MAX_KMH,
    SKIFF_PLANING_KMH,
  );
  boat.turnM = clampMeasure(boat.turnM, BOAT_REACH_MIN_M, BOAT_REACH_MAX_M, SKIFF_REACH_M);
  boat.accelM = clampMeasure(boat.accelM, BOAT_REACH_MIN_M, BOAT_REACH_MAX_M, SKIFF_REACH_M);
  boat.brakeM = clampMeasure(boat.brakeM, BOAT_REACH_MIN_M, BOAT_REACH_MAX_M, SKIFF_REACH_M);
  boat.hull = clampRgba(boat.hull);
  boat.deck = clampRgba(boat.deck);
  boat.accent = clampRgba(boat.accent);
  for (const part of SIDE_VIEW_PARTS) {
    boat.side[part.key] = clampRgba(boat.side[part.key]);
  }
}
